import { useMemo, useState } from 'react';
import { formatMm, type EvaluatedProject, type ProjectDocument } from '@scs/domain-engine';
import { buildCutList } from '@scs/export-contracts';
import { t } from '../lib/i18n';
import { lookupFor, profileFor, useCatalogContext } from '../lib/catalog';

type Tab = 'issues' | 'parts' | 'cutlist' | 'edges';

export function BottomPanel(props: {
  doc: ProjectDocument;
  ev: EvaluatedProject;
  projectId: string;
  savedRevision: number;
  dirty: boolean;
  onSelect(cabinetId: string, partId: string | null): void;
}) {
  const [tab, setTab] = useState<Tab>('issues');
  const catalog = useCatalogContext();
  const cut = useMemo(
    () => buildCutList(props.doc, props.ev, { catalog: lookupFor(catalog), profile: profileFor(catalog, props.doc.settings.measurementProfileId) }),
    [props.doc, props.ev, catalog],
  );
  const errors = props.ev.issues.filter((i) => i.severity === 'error').length;
  const warnings = props.ev.issues.filter((i) => i.severity === 'warning').length;

  return (
    <section className="panel panel-bottom" aria-label="Donji panel">
      <div className="bottom-tabs" role="tablist">
        <button className={tab === 'issues' ? 'on' : ''} onClick={() => setTab('issues')}>
          {t('editor.issues')} {errors > 0 && <span className="badge err">{errors}</span>} {warnings > 0 && <span className="badge warn">{warnings}</span>}
        </button>
        <button className={tab === 'parts' ? 'on' : ''} onClick={() => setTab('parts')}>
          {t('editor.parts')} <span className="badge">{cut.totals.parts}</span>
        </button>
        <button className={tab === 'cutlist' ? 'on' : ''} onClick={() => setTab('cutlist')}>
          {t('editor.cutlist')}
        </button>
        <button className={tab === 'edges' ? 'on' : ''} onClick={() => setTab('edges')}>
          {t('editor.edges')}
        </button>
        <div style={{ flex: 1 }} />
        {tab === 'cutlist' && (
          <a className="btn sm" style={{ marginBottom: 3 }} href={`/api/projects/${props.projectId}/cutlist.csv?revision=${props.savedRevision}`} title={props.dirty ? 'Nesnimljene izmjene nisu uključene' : undefined}>
            {t('editor.downloadCsv')} · rev {props.savedRevision}
            {props.dirty ? ' *' : ''}
          </a>
        )}
      </div>
      <div className="bottom-body">
        {tab === 'issues' && (
          <table className="grid">
            <tbody>
              {props.ev.issues.length === 0 && (
                <tr>
                  <td className="muted">Nema problema.</td>
                </tr>
              )}
              {props.ev.issues.map((i, n) => (
                <tr key={n} className="click" onClick={() => i.cabinetId && props.onSelect(i.cabinetId, i.partId ?? null)}>
                  <td style={{ width: 90 }}>
                    <span className={`sev ${i.severity}`} />
                    {i.severity === 'error' ? 'Greška' : i.severity === 'warning' ? 'Upozorenje' : 'Info'}
                  </td>
                  <td className="mono faint" style={{ width: 160 }}>
                    {i.partId ?? i.cabinetId ?? ''}
                  </td>
                  <td style={{ whiteSpace: 'normal' }}>{i.message}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {tab === 'parts' && (
          <table className="grid">
            <thead>
              <tr>
                <th>ID</th>
                <th>Dio</th>
                <th>Dužina</th>
                <th>Širina</th>
                <th>Debljina</th>
                <th>God</th>
              </tr>
            </thead>
            <tbody>
              {props.ev.cabinets.flatMap((c) =>
                c.result.parts.map((p) => (
                  <tr key={p.id} className="click" onClick={() => props.onSelect(c.instance.id, p.id)}>
                    <td className="mono">{p.id}</td>
                    <td>{p.label}</td>
                    <td className="n">{formatMm(p.length)}</td>
                    <td className="n">{formatMm(p.width)}</td>
                    <td className="n">{formatMm(p.thickness)}</td>
                    <td>{p.grain === 'none' ? '—' : p.grain === 'length' ? 'dužina' : 'širina'}</td>
                  </tr>
                )),
              )}
            </tbody>
          </table>
        )}
        {tab === 'cutlist' && (
          <table className="grid">
            <thead>
              <tr>
                <th>Rb</th>
                <th>Dio</th>
                <th>Materijal</th>
                <th>Deb.</th>
                <th>Kom</th>
                <th title="Mjera za narudžbu prema profilu">Dužina (nar.)</th>
                <th title="Mjera za narudžbu prema profilu">Širina (nar.)</th>
                <th>Gotova</th>
                <th>L1</th>
                <th>L2</th>
                <th>W1</th>
                <th>W2</th>
              </tr>
            </thead>
            <tbody>
              {cut.rows.map((r, i) => (
                <tr key={r.signature}>
                  <td className="n">{i + 1}</td>
                  <td title={r.partIds.join(', ')}>{r.label}</td>
                  <td>{r.materialName}</td>
                  <td className="n">{formatMm(r.thickness)}</td>
                  <td className="n">{r.quantity}</td>
                  <td className="n">{formatMm(r.order.length)}</td>
                  <td className="n">{formatMm(r.order.width)}</td>
                  <td className="n faint">
                    {formatMm(r.finished.length)} × {formatMm(r.finished.width)}
                  </td>
                  {(['L1', 'L2', 'W1', 'W2'] as const).map((e) => (
                    <td key={e} className="n" style={{ color: r.edges[e].decision === 'band' ? 'var(--edge-band)' : r.edges[e].decision === 'unresolved' ? 'var(--edge-unresolved)' : r.edges[e].decision === 'seal' ? 'var(--info)' : 'var(--text-faint)' }} title={r.edges[e].decision === 'seal' ? 'Zaptivanje reza' : r.edges[e].decision === 'factory' ? 'Fabrička ivica' : undefined}>
                      {r.edges[e].decision === 'band' ? formatMm(r.edges[e].thickness) : r.edges[e].decision === 'unresolved' ? '?' : r.edges[e].decision === 'seal' ? 'Z' : r.edges[e].decision === 'factory' ? 'F' : '—'}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {tab === 'cutlist' && cut.cnc.length > 0 && (
          <>
            <h4 className="cnc-head">CNC obrada — nepravilni oblici (mjere su gabarit)</h4>
            <table className="grid">
              <thead>
                <tr>
                  <th>Rb</th>
                  <th>Dio</th>
                  <th>Element</th>
                  <th>Materijal</th>
                  <th>Deb.</th>
                  <th>Gabarit (nar.)</th>
                  <th title="Pravougaonici od donjeg lijevog ugla fronte, mm">Obris</th>
                </tr>
              </thead>
              <tbody>
                {cut.cnc.map((r, i) => (
                  <tr key={r.signature}>
                    <td className="n">C{i + 1}</td>
                    <td>{r.label}</td>
                    <td>{r.cabinetNames.join(', ')}</td>
                    <td>{r.materialName}</td>
                    <td className="n">{formatMm(r.thickness)}</td>
                    <td className="n">
                      {formatMm(r.order.length)} × {formatMm(r.order.width)}
                    </td>
                    <td className="faint">{r.cnc!.outline.map((o) => `${formatMm(o.x0)}–${formatMm(o.x1)} / ${formatMm(o.y0)}–${formatMm(o.y1)}`).join(' + ')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
        {tab === 'edges' && (
          <table className="grid">
            <thead>
              <tr>
                <th>Traka</th>
                <th>Šifra</th>
                <th>Debljina × širina</th>
                <th>Ivica</th>
                <th>Neto</th>
                <th title="Neto + prepust po ivici, plus otpad (prema profilu)">Bruto</th>
              </tr>
            </thead>
            <tbody>
              {cut.totals.edgeBands.map((b) => (
                <tr key={b.key}>
                  <td>{b.name}</td>
                  <td className="mono">{b.code || '—'}</td>
                  <td className="n">
                    {formatMm(b.thickness)} × {formatMm(b.width)} mm
                  </td>
                  <td className="n">{b.edges}</td>
                  <td className="n">{(b.net / 1_000_000).toFixed(2)} m</td>
                  <td className="n">{(b.gross / 1_000_000).toFixed(2)} m</td>
                </tr>
              ))}
              {cut.totals.sealLength > 0 && (
                <tr>
                  <td colSpan={4}>Zaptivanje rezova (mokri čvor) — nije kant traka</td>
                  <td className="n">{(cut.totals.sealLength / 1_000_000).toFixed(2)} m</td>
                  <td />
                </tr>
              )}
              <tr>
                <td colSpan={6} className="faint">
                  Profil mjera: {cut.profile.label} (prepust {formatMm(cut.profile.edgeOverhangPerEdge)} mm/ivici, otpad {cut.profile.edgeWastePercent}%). Neriješenih ivica: {cut.totals.unresolvedEdges}.
                </td>
              </tr>
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}
